import React from 'react';

const OnboardingProgressIndicator = ({ 
  steps, 
  currentStep, 
  completedSteps = [], 
  className = "",
  showLabels = true,
  showDescription = true,
  variant = 'horizontal' // 'horizontal' or 'vertical'
}) => {
  const getStepStatus = (stepIndex) => {
    if (completedSteps.includes(stepIndex + 1)) {
      return 'completed';
    } else if (stepIndex + 1 === currentStep) {
      return 'current';
    } else if (stepIndex + 1 < currentStep) {
      return 'completed';
    } else {
      return 'upcoming';
    }
  };

  const getStepStyles = (status) => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-teal-600 border-teal-600 text-white',
          connector: 'bg-teal-600',
          label: 'text-gray-900',
          description: 'text-gray-600'
        };
      case 'current':
        return {
          circle: 'border-teal-600 text-teal-600 bg-white ring-2 ring-teal-600 ring-offset-2',
          connector: 'bg-gray-300',
          label: 'text-teal-600 font-semibold',
          description: 'text-teal-600'
        };
      case 'upcoming':
        return {
          circle: 'border-gray-300 text-gray-400 bg-white',
          connector: 'bg-gray-300',
          label: 'text-gray-400',
          description: 'text-gray-400'
        };
      default:
        return {
          circle: 'border-gray-300 text-gray-400 bg-white',
          connector: 'bg-gray-300',
          label: 'text-gray-400',
          description: 'text-gray-400'
        };
    }
  };

  const renderStepIcon = (step, status, index) => {
    const styles = getStepStyles(status);
    
    return (
      <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200 ${styles.circle}`}>
        {status === 'completed' ? (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : status === 'current' ? (
          <div className="w-2 h-2 bg-teal-600 rounded-full animate-pulse" />
        ) : (
          <span className="text-sm font-medium">{index + 1}</span>
        )}
      </div>
    );
  };

  const renderConnector = (index, status) => {
    if (index === steps.length - 1) return null;
    
    const styles = getStepStyles(status);
    const nextStatus = getStepStatus(index + 1);
    const connectorColor = status === 'completed' ? 'bg-teal-600' : 'bg-gray-300';
    
    if (variant === 'vertical') {
      return (
        <div className="flex justify-center">
          <div className={`w-0.5 h-8 ${connectorColor} transition-colors duration-200`} />
        </div>
      );
    }
    
    return (
      <div className={`hidden sm:block w-16 h-0.5 ${connectorColor} transition-colors duration-200`} />
    );
  };

  const renderStepContent = (step, status, index) => {
    const styles = getStepStyles(status);
    
    return (
      <div className={variant === 'vertical' ? 'ml-4 pb-8' : 'ml-3'}>
        {showLabels && (
          <p className={`text-sm font-medium transition-colors duration-200 ${styles.label}`}>
            {step.title}
          </p>
        )}
        {showDescription && step.description && (
          <p className={`text-xs mt-1 transition-colors duration-200 ${styles.description}`}>
            {step.description}
          </p>
        )}
      </div>
    );
  };

  if (variant === 'vertical') {
    return (
      <div className={`space-y-0 ${className}`}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          
          return (
            <div key={step.id || index} className="relative">
              <div className="flex items-start">
                {renderStepIcon(step, status, index)}
                {renderStepContent(step, status, index)}
              </div>
              {renderConnector(index, status)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between ${className}`}>
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        
        return (
          <div key={step.id || index} className="flex items-center">
            <div className="flex flex-col items-center">
              {renderStepIcon(step, status, index)}
              {renderStepContent(step, status, index)}
            </div>
            {renderConnector(index, status)}
          </div>
        );
      })}
    </div>
  );
};

// Progress bar variant for simpler use cases
export const OnboardingProgressBar = ({ 
  currentStep, 
  totalSteps, 
  className = "",
  showPercentage = true,
  animated = true
}) => {
  const percentage = Math.round((currentStep / totalSteps) * 100);
  
  return (
    <div className={`w-full ${className}`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">
          Step {currentStep} of {totalSteps}
        </span>
        {showPercentage && (
          <span className="text-sm text-gray-500">
            {percentage}%
          </span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`bg-teal-600 h-2 rounded-full transition-all duration-500 ease-out ${animated ? 'animate-pulse' : ''}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Circular progress indicator
export const OnboardingCircularProgress = ({ 
  currentStep, 
  totalSteps, 
  size = 80,
  strokeWidth = 8,
  className = ""
}) => {
  const percentage = (currentStep / totalSteps) * 100;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="text-teal-600 transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-semibold text-gray-700">
          {currentStep}/{totalSteps}
        </span>
      </div>
    </div>
  );
};

export default OnboardingProgressIndicator;
